/**
 * ViewMDOrderDialog - View MD Order Dialog Component
 * Extracted from ClientDetailPage
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";

import { DISCHARGE_REASONS } from "./DischargeOrderFields";

export function ViewMDOrderDialog({
  open,
  onOpenChange,
  viewingMdOrder,
  onEdit,
}) {
  if (!viewingMdOrder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>MD Order Details</DialogTitle>
          <DialogDescription>
            Order #{viewingMdOrder.id?.slice(-8)} • Created {viewingMdOrder.created_at?.split('T')[0]}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Order Types */}
          <div className="flex flex-wrap gap-2">
            {viewingMdOrder.order_types?.map((type) => (
              <span key={type} className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                {type === 'personal_care' 
                  ? 'Personal Care Service Evaluation and Treatment' 
                  : type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            ))}
          </div>

          {/* Status */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Status:</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                viewingMdOrder.physician_signature ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {viewingMdOrder.physician_signature ? 'Signed' : 'Pending Signature'}
              </span>
            </div>
            {viewingMdOrder.physician_signature && (
              <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-700 font-medium mb-2">
                  ✓ Signed by {viewingMdOrder.physician_signer_name} on {viewingMdOrder.physician_signature_date?.split('T')[0]}
                </p>
                {viewingMdOrder.physician_npi && (
                  <p className="text-xs text-emerald-600 mb-2">NPI: {viewingMdOrder.physician_npi}</p>
                )}
                {viewingMdOrder.physician_signature.startsWith('data:image') && (
                  <div className="mt-2 p-2 bg-white rounded border border-emerald-200">
                    <p className="text-xs text-slate-500 mb-1">Signature:</p>
                    <img 
                      src={viewingMdOrder.physician_signature} 
                      alt="Physician Signature" 
                      className="max-h-20 object-contain"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Physician Info */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg">
            <div>
              <p className="text-xs text-slate-500">Physician Name</p>
              <p className="text-sm font-medium">{viewingMdOrder.physician_name || "Not specified"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Phone</p>
              <p className="text-sm font-medium">{viewingMdOrder.physician_phone || "Not specified"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Fax</p>
              <p className="text-sm font-medium">{viewingMdOrder.physician_fax || "Not specified"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">NPI</p>
              <p className="text-sm font-medium">{viewingMdOrder.physician_npi || "Not specified"}</p>
            </div>
          </div>

          {/* Personal Care Section */}
          {viewingMdOrder.order_types?.includes('personal_care') && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Personal Care Service Evaluation and Treatment and Last 3 MD visit notes</h4>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-slate-500">Last 3 MD Office Visit Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{viewingMdOrder.md_visit_notes || "Not provided"}</p>
                </div>
              </div>
            </div>
          )}

          {/* DME Details */}
          {viewingMdOrder.order_types?.includes('dme_supplies') && (
            <div className="p-3 bg-purple-50 rounded-lg">
              <h4 className="text-sm font-medium text-purple-800 mb-2">DME/Supplies</h4>
              <p className="text-sm">{viewingMdOrder.dme_other || "No items specified"}</p>
            </div>
          )}

          {/* Service Hold Details */}
          {viewingMdOrder.order_types?.includes('service_hold') && (
            <div className="p-3 bg-amber-50 rounded-lg">
              <h4 className="text-sm font-medium text-amber-800 mb-2">Service Hold Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Hold Start Date</p>
                  <p className="font-medium" data-testid="view-hold-start">{viewingMdOrder.hold_start_date || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Expected Return</p>
                  <p className="font-medium" data-testid="view-hold-return">{viewingMdOrder.hold_expected_return || "N/A"}</p>
                </div>
                {viewingMdOrder.hold_reason && (
                  <div>
                    <p className="text-xs text-slate-500">Reason (internal)</p>
                    <p className="font-medium capitalize">
                      {viewingMdOrder.hold_reason.replace(/_/g, ' ')}
                      {viewingMdOrder.hold_reason_other ? ` — ${viewingMdOrder.hold_reason_other}` : ""}
                    </p>
                  </div>
                )}
                {viewingMdOrder.hold_instructions && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Additional Physician Instructions / Follow-Up</p>
                    <p className="font-medium whitespace-pre-wrap" data-testid="view-hold-instructions">{viewingMdOrder.hold_instructions}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Discharge Details */}
          {viewingMdOrder.order_types?.includes('discharge') && (
            <div className="p-3 bg-red-50 rounded-lg">
              <h4 className="text-sm font-medium text-red-800 mb-2">Discharge Details</h4>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Reason</p>
                  <p className="font-medium" data-testid="view-discharge-reason">
                    {DISCHARGE_REASONS[viewingMdOrder.discharge_reason] || "N/A"}
                    {viewingMdOrder.discharge_reason_other ? ` — ${viewingMdOrder.discharge_reason_other}` : ""}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Effective Discharge Date</p>
                    <p className="font-medium">{viewingMdOrder.discharge_date || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Last Date of Service</p>
                    <p className="font-medium">{viewingMdOrder.discharge_last_service_date || "N/A"}</p>
                  </div>
                </div>
                {viewingMdOrder.discharge_instructions && (
                  <div>
                    <p className="text-xs text-slate-500">Additional Physician Instructions / Follow-Up</p>
                    <p className="font-medium whitespace-pre-wrap">{viewingMdOrder.discharge_instructions}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {viewingMdOrder.notes && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <h4 className="text-sm font-medium text-slate-800 mb-2">Notes</h4>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{viewingMdOrder.notes}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          {!viewingMdOrder.physician_signature && onEdit && (
            <Button 
              variant="outline" 
              onClick={() => {
                onOpenChange(false);
                onEdit(viewingMdOrder);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Order
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
